/**
 * Projects API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Project,
  ProjectCreateData,
  ProjectUpdateData,
  ProjectFilters,
  Task,
  TimeEntry,
} from '@/types'

export const projectsApi = {
  /**
   * List projects with filtering and pagination
   */
  async list(params?: PaginationParams & ProjectFilters): Promise<PaginatedResponse<Project>> {
    const response = await apiClient.get<PaginatedResponse<Project>>('/projects', { params })
    return response.data
  },

  /**
   * Get project by ID
   */
  async get(id: string): Promise<Project> {
    const response = await apiClient.get<ApiResponse<Project>>(`/projects/${id}`)
    return response.data.data
  },

  /**
   * Create a new project
   */
  async create(data: ProjectCreateData): Promise<Project> {
    const response = await apiClient.post<ApiResponse<Project>>('/projects', data)
    return response.data.data
  },

  /**
   * Update a project
   */
  async update(id: string, data: ProjectUpdateData): Promise<Project> {
    const response = await apiClient.put<ApiResponse<Project>>(`/projects/${id}`, data)
    return response.data.data
  },

  /**
   * Delete a project
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/projects/${id}`)
  },

  /**
   * Get project tasks
   */
  async getTasks(id: string, params?: PaginationParams): Promise<PaginatedResponse<Task>> {
    const response = await apiClient.get<PaginatedResponse<Task>>(`/projects/${id}/tasks`, { params })
    return response.data
  },

  /**
   * Get project time entries
   */
  async getTimeEntries(id: string, params?: PaginationParams): Promise<PaginatedResponse<TimeEntry>> {
    const response = await apiClient.get<PaginatedResponse<TimeEntry>>(`/projects/${id}/time-entries`, { params })
    return response.data
  },

  /**
   * Add team member to project
   */
  async addTeamMember(id: string, userId: string): Promise<Project> {
    const response = await apiClient.post<ApiResponse<Project>>(`/projects/${id}/team`, { user_id: userId })
    return response.data.data
  },

  /**
   * Remove team member from project
   */
  async removeTeamMember(id: string, userId: string): Promise<Project> {
    const response = await apiClient.delete<ApiResponse<Project>>(`/projects/${id}/team/${userId}`)
    return response.data.data
  },
}
